import { ExternalLink } from "lucide-react";
import { ReportHeader } from "@/components/report-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ViewSelector } from "@/components/view-selector";
import { SavedViewProvider } from "@/contexts/saved-view-context";
import { useCampaignsByIds } from "@/hooks/api/use-campaigns";
import { useExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import { useBenchmarkUI } from "@/hooks/use-benchmark-ui";
import { useCampaignSelection } from "@/hooks/use-campaign-selection";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  getExternalConnectionLink,
  getExternalSystemName,
} from "@/lib/external-connection-links";
// moved into report card content
// import { CampaignStatusSelector } from "./campaign-status-selector";
// import { BenchmarkToggle } from "./benchmark-toggle";
import { BenchmarkConfigModal } from "./benchmark-config-modal";
import { CampaignScorecard } from "./campaign-scorecard";
import { CampaignSelector } from "./campaign-selector";

const SOURCES_THAT_DONT_REQUIRE_STATUS = new Set(["hubspot.email_campaign"]);

export default function CampaignsPage() {
  const pageType = "campaigns::explorer";
  const { selectedCampaignId } = useCampaignSelection();
  const { data: campaignData, isLoading: isCampaignLoading } =
    useCampaignsByIds(selectedCampaignId ? [selectedCampaignId] : undefined);
  const { data: connections, isLoading: isLoadingConnections } =
    useExternalConnectionLinks();
  const campaign = campaignData?.results?.[0];
  const requiresStatus = !SOURCES_THAT_DONT_REQUIRE_STATUS.has(
    campaign?.source_record_type || "",
  );

  // Use the benchmark UI hook to manage benchmark configuration
  const {
    benchmarkConfig,
    isBenchmarkEnabled,
    showBenchmarkModal,
    hasBenchmarkConfig,
    setShowBenchmarkModal,
    handleSaveBenchmarkConfig,
    handleBenchmarkToggleChange,
    openBenchmarkModal,
  } = useBenchmarkUI();

  // Set page title based on selected campaign
  usePageTitle(
    campaign ? `${campaign.name} - Campaign Report` : "Campaign Report Card",
  );

  return (
    <SavedViewProvider pageType={pageType}>
      <div className="flex flex-col space-y-4">
        <ReportHeader
          title="Campaign Report Card"
          primary={
            <div className="flex flex-wrap gap-2">
              <CampaignSelector />
            </div>
          }
          right={
            selectedCampaignId && !isCampaignLoading && campaign ? (
              <ViewSelector pageType={pageType} />
            ) : undefined
          }
        />

        {isCampaignLoading ? null : !selectedCampaignId ? (
          <EmptyState
            title="Select a campaign"
            description="Select a campaign from the dropdown above to view its report card."
          />
        ) : (
          <Tabs defaultValue="scorecard" className="w-full">
            <div className="flex items-center border-b">
              <TabsList className="border-b-0">
                <TabsTrigger value="scorecard" className="relative">
                  <div className="flex items-center gap-1.5">Scorecard</div>
                </TabsTrigger>
              </TabsList>
              {selectedCampaignId &&
                campaign &&
                (isLoadingConnections ? (
                  <Skeleton className="h-4 w-4 ml-2" />
                ) : (
                  (() => {
                    const url = getExternalConnectionLink(
                      campaign,
                      connections,
                    );
                    if (!url) return null;
                    return (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors ml-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            View in{" "}
                            {getExternalSystemName(campaign.source_record_type)}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })()
                ))}
            </div>

            <TabsContent value="scorecard" className="mt-6">
              <CampaignScorecard
                benchmarkConfig={benchmarkConfig}
                isBenchmarkEnabled={isBenchmarkEnabled}
                hasBenchmarkConfig={hasBenchmarkConfig}
                onBenchmarkToggleChange={handleBenchmarkToggleChange}
                onOpenBenchmarkModal={openBenchmarkModal}
                requiresStatus={requiresStatus}
              />
            </TabsContent>
            <TabsContent value="accounts">
              <div className="text-center py-8 text-muted-foreground">
                Accounts tab content coming soon
              </div>
            </TabsContent>
            <TabsContent value="opportunities">
              <div className="text-center py-8 text-muted-foreground">
                Opportunities tab content coming soon
              </div>
            </TabsContent>
            <TabsContent value="people">
              <div className="text-center py-8 text-muted-foreground">
                People tab content coming soon
              </div>
            </TabsContent>
            <TabsContent value="touchpoints">
              <div className="text-center py-8 text-muted-foreground">
                Touchpoints tab content coming soon
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Benchmark configuration modal */}
        <BenchmarkConfigModal
          open={showBenchmarkModal}
          onOpenChange={setShowBenchmarkModal}
          onSaveConfig={handleSaveBenchmarkConfig}
          initialFilters={benchmarkConfig.filters}
          initialExcludeCurrentCampaign={benchmarkConfig.excludeCurrentCampaign}
          initialMode={benchmarkConfig.mode || "static"}
          initialSelectedCampaignIds={benchmarkConfig.selectedCampaignIds || []}
        />
      </div>
    </SavedViewProvider>
  );
}
